import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Lock, CircleUserRound } from "lucide-react";
import { NAV_LINKS, LOGOS } from "../lib/brand";
import { ScallopedBadge } from "./Decor2";
import { BubbleEdge } from "./Wavy";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-shadow duration-300 ${
        scrolled ? "shadow-[0_18px_50px_-26px_rgba(74,27,77,0.55)]" : ""
      }`}
    >
      <div className="relative bg-magenta pb-3">
        <div className="absolute left-0 right-0 bottom-0 pointer-events-none">
          <BubbleEdge color="#FDFBF7" bump={26} />
        </div>

        <div className="relative max-w-[1500px] mx-auto px-5 md:px-10 h-[78px] md:h-[92px] flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="inline-flex items-center gap-3 group">
            <ScallopedBadge fill="#FDFBF7" scallops={18} className="w-11 h-11 md:w-12 md:h-12 shrink-0">
              <img src={LOGOS.bloomFoundry} alt="" className="w-[78%] h-[78%] object-contain" draggable="false" />
            </ScallopedBadge>
            <div className="leading-none">
              <span className="block font-serif-display uppercase text-cream text-[17px] md:text-[22px] tracking-[0.06em]">
                THE BLOOM FOUNDRY
              </span>
              <span className="block font-serif-display uppercase text-cream/75 text-[9px] md:text-[10.5px] tracking-[0.32em] mt-1">
                HERITAGE × THE ETCHERY
              </span>
            </div>
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-cream"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>

          <nav className="hidden lg:flex items-center gap-6 z-10">
            {NAV_LINKS.map((l) => (
              <NavItem key={l.to} to={l.to} label={l.label} />
            ))}
            <Link
              to="/account"
              data-testid="nav-account"
              aria-label="Foundry Profile"
              className="text-cream/90 hover:text-lemon transition-colors"
            >
              <CircleUserRound className="w-6 h-6" />
            </Link>
            <Link
              to="/console/login"
              data-testid="nav-foundry-console-login"
              className="ml-2 inline-flex items-center gap-2 rounded-full bg-cream text-magenta px-4 py-2.5 font-serif-display uppercase text-[12px] tracking-[0.14em] font-semibold hover:bg-lemon transition-colors shadow-[0_8px_20px_-12px_rgba(74,27,77,0.6)]"
            >
              <Lock className="w-3.5 h-3.5" /> CONSOLE
            </Link>
          </nav>
        </div>

        {open && (
          <div data-testid="mobile-menu" className="lg:hidden bg-magenta-deep/95 backdrop-blur-sm px-6 pb-8 pt-2">
            <ul className="space-y-4">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setOpen(false)}
                    className="block font-serif-display uppercase text-cream tracking-[0.18em] text-base py-2 border-b border-cream/15"
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
              <li>
                <Link
                  to="/account"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-account"
                  className="flex items-center gap-2 font-serif-display uppercase text-cream tracking-[0.18em] text-base py-2 border-b border-cream/15"
                >
                  <CircleUserRound className="w-4 h-4" /> MY PROFILE
                </Link>
              </li>
              <li>
                <Link
                  to="/console/login"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-foundry-console-login"
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-cream text-magenta px-5 py-2.5 font-serif-display uppercase text-sm tracking-[0.14em] font-semibold"
                >
                  <Lock className="w-3.5 h-3.5" /> FOUNDRY CONSOLE
                </Link>
              </li>
            </ul>
          </div>
        )}
      </div>
    </header>
  );
};

const NavItem = ({ to, label }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    className="relative text-cream/95 hover:text-cream font-serif-display uppercase tracking-[0.14em] text-[13.5px] font-semibold transition-colors"
  >
    {({ isActive }) => (
      <span className="relative">
        {label}
        {isActive && <span className="absolute -bottom-2 left-0 right-0 h-[2px] bg-cream/80" />}
      </span>
    )}
  </NavLink>
);

export default Header;
